/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable @typescript-eslint/no-shadow */

import { useCallback, useEffect, useRef, useState } from "react"
import { debounce, includes, uniqBy } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import {
  FILTER_LIMIT,
  SEARCH_LIMIT,
  STORE_LIST_LIMIT,
} from "components/search/searchBarConstants"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
  FilterTrigger,
} from "constants/tracking"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { MerchantStoreInfo, StoreFilter } from "types/models/menu"
import { StoreMigrationMissingInfoSummary, MerchantAggregate } from "types/api"
import { actionCreator } from "store/duck/menu"

import {
  flattenInfiniteOutlets,
  useSearchOutletsPaginatedInfiniteQuery,
  useFetchMerchantAggregateQuery,
} from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

type FilterTrackerData = { trigger: FilterTrigger; category: EventCategory }

const ALL = "ALL"

const isFilterCityActive = (storeFilter: StoreFilter) => {
  return !!storeFilter?.city?.length && !storeFilter?.city?.includes(ALL)
}

const useMenuStoreList = (
  outletsOverride?: MerchantStoreInfo[],
  includeMenuGroupV2ID?: boolean,
) => {
  const dispatch = useDispatch()
  const {
    flags: {
      searchOutlets: isSearchOutletsRtkEnabled,
      fetchItemsWithoutPhotoCount: isFetchItemsWithoutPhotoCountRtkEnabled,
      fetchMerchantAggregate: isFetchMerchantAggregateEnabled,
    },
    isLoading: isFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [inputValue, setInputValue] = useState<string>("")

  const [filter, setFilter] = useState<StoreFilter>({
    search: "",
    city: [ALL],
    asc: undefined,
  })

  // Stores tracking data for the pending filter change.
  // Fired in useEffect once the first page of results arrives.
  const pendingTrackRef = useRef<{
    filterObj: StoreFilter
    trackData: { trigger: FilterTrigger; category: EventCategory }
  } | null>(null)

  const {
    menu: {
      cityList: reduxCityList,
      outletsSearchHasMore: outletsSearchHasMoreRedux,
      outletsSearch: outletsSearchRedux = [],
      migrationMissingInfoSummary: migrationMissingInfoSummaryRedux,
      isFetchingMoreOutlets: isFetchingMoreOutletsRedux,
      isFetchingOutletsSearch: isFetchingOutletsSearchRedux,
      merchantGroupID,
    },
  } = useSelector((state: AppState) => state)

  const {
    data: outletsInfiniteData,
    isLoading: isFetchingOutletsSearchRTK,
    isError: isFetchingOutletsSearchErrorRTK,
    isFetchingNextPage: isFetchingMoreOutletsRTK,
    fetchNextPage,
    hasNextPage,
  } = useSearchOutletsPaginatedInfiniteQuery(
    {
      search: filter.search.trim(),
      limit: filter.search.length ? FILTER_LIMIT : STORE_LIST_LIMIT,
      includeItemsWithoutPhotosCount: true,
      includeMenuGroupV2ID,
      asc: filter.asc,
      city: filter.city,
      modelType: "integrated",
    },
    { skip: !isSearchOutletsRtkEnabled || !merchantGroupID },
  )

  const { data: merchantAggregateData } = useFetchMerchantAggregateQuery(
    {
      merchantGroupID,
      fields: [MerchantAggregate.cityID, MerchantAggregate.businessType],
    },
    { skip: !isFetchMerchantAggregateEnabled || !merchantGroupID },
  )

  // Derived RTK state — replaces the old manual useState accumulators
  const outletsSearchRTK = flattenInfiniteOutlets(outletsInfiniteData)
  const outletsSearchHasMoreRTK = hasNextPage ?? false

  // Use RTK Query state or Redux state based on feature flag
  const outletsSearch = isSearchOutletsRtkEnabled
    ? outletsSearchRTK
    : outletsSearchRedux
  const outletsSearchHasMore = isSearchOutletsRtkEnabled
    ? outletsSearchHasMoreRTK
    : outletsSearchHasMoreRedux
  const isFetchingOutletsSearch = isSearchOutletsRtkEnabled
    ? isFetchingOutletsSearchRTK
    : isFetchingOutletsSearchRedux
  const isFetchingMoreOutlets = isSearchOutletsRtkEnabled
    ? isFetchingMoreOutletsRTK
    : isFetchingMoreOutletsRedux

  const cityList = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.cities
    : reduxCityList

  const getCityListOption = () => {
    if (outletsOverride)
      return uniqBy(
        outletsOverride?.map(({ city }) => ({
          text: city,
          value: city,
        })),
        "value",
      )

    return [
      ...(cityList || [])?.map(({ cityName, cityID }) => ({
        text: cityName,
        value: `${cityID}`,
      })),
    ]
  }

  const filterMerchantByCity = (merchant: MerchantStoreInfo): boolean => {
    if (!isFilterCityActive(filter)) return true

    const cityNames = filter?.city.map(
      item =>
        (cityList || []).find(city => city.cityID.toString() === item)
          ?.cityName,
    )

    return cityNames.includes(merchant.city)
  }

  const filterByCityAndSearch = (merchant: MerchantStoreInfo) => {
    return (
      filterMerchantByCity(merchant) &&
      includes(
        merchant.merchantName?.toLowerCase(),
        filter?.search?.toLowerCase(),
      )
    )
  }

  const showLoadMore = outletsSearchHasMore && !isFetchingOutletsSearch

  // RTK Query path migration tracking — fires once flag resolves to enabled
  useEffect(() => {
    if (!isFlagLoading && isSearchOutletsRtkEnabled) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.searchOutletsPaginatedInfinite,
        labels: {
          IS_RTK_ENABLED: isSearchOutletsRtkEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "searchOutletsPaginatedInfinite",
        },
      })
    }
  }, [isFlagLoading, isSearchOutletsRtkEnabled])

  // For RTK Query Path in handlerFilter, the useEffect triggered when filter is changed
  useEffect(() => {
    if (!isSearchOutletsRtkEnabled) return
    if (!pendingTrackRef.current) return
    if (!outletsInfiniteData?.pages?.[0]) return

    const { filterObj, trackData } = pendingTrackRef.current
    pendingTrackRef.current = null

    const merchantStore: MerchantStoreInfo[] =
      outletsInfiniteData.pages[0].merchants || []
    TroyWebTracker.sendEvent({
      category: trackData.category,
      action: EventAction.searchStore,
      labels: {
        FILTER_TEXT: filterObj.search,
        FILTER_ASC: filterObj.asc ?? true,
        FILTER_CITY: filterObj.city,
        TRIGGER: trackData.trigger,
        RESULT: merchantStore.map(item => item.merchantName).toString(),
        TOTAL_RESULT: merchantStore.length,
      },
    })
  }, [isSearchOutletsRtkEnabled, outletsInfiniteData])

  const filteredList = (() => {
    if (outletsOverride)
      return uniqBy(outletsOverride.filter(filterByCityAndSearch), "merchantID")
    if (isFetchingOutletsSearch || filter?.search?.length > SEARCH_LIMIT)
      return []
    return outletsSearch
  })()

  const migrationMissingInfoSummary:
    | StoreMigrationMissingInfoSummary
    | undefined = isSearchOutletsRtkEnabled
    ? outletsInfiniteData?.pages?.[0]?.migrationMissingInfo
    : migrationMissingInfoSummaryRedux

  const isStoreListError = isSearchOutletsRtkEnabled
    ? isFetchingOutletsSearchErrorRTK
    : false

  const handleFilter = useCallback(
    async (
      filterObj: StoreFilter,
      { trigger, category }: FilterTrackerData,
    ) => {
      setFilter(filterObj)

      if (isSearchOutletsRtkEnabled) {
        // RTK Query path: updating filter state is enough.
        // useSearchOutletsInfiniteQuery detects the arg change and re-fetches
        // from page 0 automatically. Tracking fires in the useEffect above
        // once the first page of results arrives.
        pendingTrackRef.current = {
          filterObj,
          trackData: { trigger, category },
        }
        return
      }

      // Old Redux action path
      if (!merchantGroupID) return

      const body = {
        ...filterObj,
        search: filterObj.search.trim(),
        limit: filterObj?.search?.length ? FILTER_LIMIT : STORE_LIST_LIMIT,
        includeItemsWithoutPhotosCount: true,
        includeMenuGroupV2ID,
      }

      const res: any = await dispatch(actionCreator.searchOutlets(body))
      const merchantStore: MerchantStoreInfo[] =
        res?.payload?.data?.merchants || []

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.searchOutletsPaginatedInfinite,
        labels: {
          IS_RTK_ENABLED: isSearchOutletsRtkEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "searchOutletsPaginatedInfinite",
        },
      })

      TroyWebTracker.sendEvent({
        category,
        action: EventAction.searchStore,
        labels: {
          FILTER_TEXT: filterObj.search,
          FILTER_ASC: filterObj.asc ?? true,
          FILTER_CITY: filterObj.city,
          TRIGGER: trigger,
          RESULT: merchantStore.map(item => item.merchantName).toString(),
          TOTAL_RESULT: merchantStore.length,
        },
      })
    },
    [
      isSearchOutletsRtkEnabled,
      dispatch,
      includeMenuGroupV2ID,
      merchantGroupID,
    ],
  )

  const debouncedSearch = useCallback(
    debounce((filter: StoreFilter, trackParam: FilterTrackerData) => {
      handleFilter(filter, trackParam)
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [handleFilter],
  )

  const handleSearchFilter = (val: string, trackParam: FilterTrackerData) => {
    setInputValue(val)
    debouncedSearch({ ...filter, search: val.trim() }, trackParam)
  }

  const handleLoadMoreOutlets = () => {
    if (isFetchingMoreOutlets) return

    if (isSearchOutletsRtkEnabled) {
      // RTK Query path: fetchNextPage computes the next offset from
      // getNextPageParam(lastPage, allPages) — no stale-closure risk.
      fetchNextPage()
    } else {
      // Old Redux action path
      if (!merchantGroupID) return

      const body = {
        ...filter,
        search: filter.search.trim(),
        offset: outletsSearch.length,
        limit: filter.search.length ? FILTER_LIMIT : STORE_LIST_LIMIT,
        includeItemsWithoutPhotosCount: true,
        isLoadMore: true,
        includeMenuGroupV2ID,
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.searchOutletsPaginatedInfinite,
        labels: {
          IS_RTK_ENABLED: isSearchOutletsRtkEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "searchOutletsPaginatedInfinite",
        },
      })

      dispatch(actionCreator.searchOutlets(body))
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuHomePage,
      action: EventAction.loadNextPage,
    })
  }

  // Fetch merchant aggregate - with feature flag check
  useEffect(() => {
    if (!isFlagLoading && !isFetchMerchantAggregateEnabled) {
      dispatch(
        actionCreator.fetchMerchantAggregate(
          MerchantAggregate.cityID,
          MerchantAggregate.businessType,
        ),
      )
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantAggregate,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID ?? "",
          IS_RTK_ENABLED: isFetchMerchantAggregateEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "fetchMerchantAggregate",
        },
      })
    }
  }, [
    dispatch,
    isFlagLoading,
    isFetchMerchantAggregateEnabled,
    merchantGroupID,
  ])

  // RTK Query path tracking for fetchMerchantAggregate
  useEffect(() => {
    if (merchantGroupID && !isFlagLoading && isFetchMerchantAggregateEnabled) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantAggregate,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          IS_RTK_ENABLED: isFetchMerchantAggregateEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "fetchMerchantAggregate",
        },
      })
    }
  }, [merchantGroupID, isFlagLoading, isFetchMerchantAggregateEnabled])

  // Fetch items without photo count - with feature flag check
  useEffect(() => {
    if (!isFlagLoading && !isFetchItemsWithoutPhotoCountRtkEnabled) {
      dispatch(actionCreator.fetchItemsWithoutPhotoCount())
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchItemsWithoutPhotoCount,
        labels: {
          IS_RTK_ENABLED: isFetchItemsWithoutPhotoCountRtkEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "fetchItemsWithoutPhotoCount",
        },
      })
    }
  }, [dispatch, isFlagLoading, isFetchItemsWithoutPhotoCountRtkEnabled])

  // RTK Query path tracking for fetchItemsWithoutPhotoCount
  useEffect(() => {
    if (!isFlagLoading && isFetchItemsWithoutPhotoCountRtkEnabled) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchItemsWithoutPhotoCount,
        labels: {
          IS_RTK_ENABLED: isFetchItemsWithoutPhotoCountRtkEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "fetchItemsWithoutPhotoCount",
        },
      })
    }
  }, [isFlagLoading, isFetchItemsWithoutPhotoCountRtkEnabled])

  return {
    handleSearchFilter,
    handleFilter,
    filter,
    filteredList,
    handleLoadMoreOutlets,
    showLoadMore,
    inputValue,
    cityList: getCityListOption(),
    outletsSearch,
    migrationMissingInfoSummary,
    isStoreListError,
  }
}

export default useMenuStoreList
